package cz.lhoracek.mapl

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import cz.lhoracek.mapl.di.appModule
import cz.lhoracek.mapl.ui.navigation.NavigationController
import cz.lhoracek.mapl.ui.navigation.PlatformBackHandler
import cz.lhoracek.mapl.ui.navigation.Screen
import cz.lhoracek.mapl.ui.navigation.SyncNavigationWithBrowserHistory
import cz.lhoracek.mapl.ui.navigation.requestPlatformBack
import cz.lhoracek.mapl.ui.localization.ProvideAppLanguageOverride
import cz.lhoracek.mapl.ui.localization.isAppLanguageOverrideSupported
import cz.lhoracek.mapl.ui.localization.loadAppLanguageOverride
import cz.lhoracek.mapl.ui.localization.saveAppLanguageOverride
import cz.lhoracek.mapl.ui.screens.addround.AddRoundScreen
import cz.lhoracek.mapl.ui.screens.home.HomeScreen
import cz.lhoracek.mapl.ui.screens.newsession.NewSessionScreen
import cz.lhoracek.mapl.ui.screens.rules.RulesScreen
import cz.lhoracek.mapl.ui.screens.session.SessionScreen
import cz.lhoracek.mapl.ui.theme.MaplTheme
import org.koin.compose.KoinApplication
import org.koin.dsl.koinConfiguration

@Composable
fun App() {
    KoinApplication(configuration = koinConfiguration(declaration = { modules(appModule) }), content = {
        MaplTheme {
            val nav = remember { NavigationController() }
            val languageOverrideSupported = isAppLanguageOverrideSupported()
            var languageOverride by remember { mutableStateOf(loadAppLanguageOverride()) }
            val handleBack = { requestPlatformBack { nav.back() } }

            SyncNavigationWithBrowserHistory(
                stackDepth = nav.depth,
                onBackFromBrowser = { nav.back() },
            )

            // Deep link handling
            val deepLinkUrl by DeepLinkHandler.pendingUrl.collectAsState()
            LaunchedEffect(deepLinkUrl) {
                val url = deepLinkUrl ?: return@LaunchedEffect
                val sessionId = parseDeepLinkUrl(url) ?: return@LaunchedEffect
                DeepLinkHandler.consume()
                nav.backTo(Screen.Home)
                nav.navigate(Screen.Session(sessionId))
            }

            ProvideAppLanguageOverride(languageTag = languageOverride) {
                PlatformBackHandler(enabled = nav.canGoBack, onBack = handleBack) {
                    when (val screen = nav.current) {
                        Screen.Home -> HomeScreen(
                            onNewSession = { nav.navigate(Screen.NewSession) },
                            onOpenSession = { id -> nav.navigate(Screen.Session(id)) },
                            onOpenRules = { nav.navigate(Screen.Rules) },
                            showLanguageSelector = languageOverrideSupported,
                            selectedLanguageCode = languageOverride,
                            onSelectLanguageCode = { languageTag ->
                                languageOverride = languageTag
                                saveAppLanguageOverride(languageTag)
                            },
                        )

                        Screen.Rules -> RulesScreen(
                            onBack = handleBack,
                        )

                        Screen.NewSession -> NewSessionScreen(
                            onBack = handleBack,
                            onSessionCreated = { id ->
                                nav.back()
                                nav.navigate(Screen.Session(id))
                            },
                        )

                        is Screen.Session -> SessionScreen(
                            sessionId = screen.sessionId,
                            onBack = handleBack,
                            onAddRound = { nav.navigate(Screen.AddRound(screen.sessionId)) },
                        )

                        is Screen.AddRound -> AddRoundScreen(
                            sessionId = screen.sessionId,
                            onBack = handleBack,
                        )
                    }
                }
            }
        }
    })
}
